import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { confidenceColor } from "@/lib/knowledge/topicClusters";
import TopicClusterMap from "./TopicClusterMap";
import type { KnowledgeBrainSummary, TopicCluster } from "@/types/knowledge/knowledge-brain";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface KnowledgeBrainOverviewProps {
  clusters: TopicCluster[];
  summary: KnowledgeBrainSummary;
  isLoading: boolean;
  /** True when clustering only saw part of a very large library. */
  isPartial: boolean;
  selectedCluster: TopicCluster | null;
  onSelectCluster: (cluster: TopicCluster | null) => void;
  onRewrite: () => void;
  onCreateDocument: () => void;
}

/**
 * Above-the-fold view of what the Genie actually knows: topics sized by
 * coverage, coloured for confidence, with a plain-language read underneath.
 */
const KnowledgeBrainOverview = ({
  clusters,
  summary,
  isLoading,
  isPartial,
  selectedCluster,
  onSelectCluster,
  onRewrite,
  onCreateDocument,
}: KnowledgeBrainOverviewProps) => (
  <section
    className="mb-6 rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] p-[2px]"
    aria-labelledby="knowledge-brain-heading"
  >
    <div className="rounded-[14px] bg-white p-4 sm:p-5 lg:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <GenieIcon
            name="brain"
            size={28}
            color="#4E9CFF"
            secondaryColor="#FF4DB8"
            gradientEnabled
            label="Genie knowledge"
            className="mt-0.5 flex-shrink-0"
          />
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-genie-primary">
              What your Genie knows
            </p>
            <h2 id="knowledge-brain-heading" className="mt-1 text-xl font-semibold text-gray-900">
              {summary.headline}
            </h2>
            {summary.detail && (
              <p className="mt-1 max-w-3xl text-sm text-gray-600">{summary.detail}</p>
            )}
          </div>
        </div>

        <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={onRewrite}
            disabled={clusters.length === 0}
            className="h-9 gap-2 border-genie-border bg-white text-gray-700 hover:border-genie-primary/30 hover:bg-genie-primary-light hover:text-genie-primary"
          >
            <GenieIcon name="palette" size={15} color="#4E9CFF" />
            Rewrite the brain
          </Button>
          <Button
            variant="outline"
            onClick={onCreateDocument}
            disabled={clusters.length === 0}
            className="h-9 gap-2 border-genie-border bg-white text-gray-700 hover:border-genie-primary/30 hover:bg-genie-primary-light hover:text-genie-primary"
          >
            <GenieIcon name="document" size={15} color="#4E9CFF" />
            Create a document
          </Button>
        </div>
      </div>

      <div className="mt-5 rounded-xl border border-genie-border bg-gray-50/50 px-3 py-6 sm:px-6">
        {isLoading ? (
          <div className="flex items-center justify-center gap-3 py-8 text-sm text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin text-genie-primary" />
            Reading your knowledge…
          </div>
        ) : clusters.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">
            Nothing to map yet. Add a source and the topics appear here.
          </p>
        ) : (
          <TopicClusterMap
            clusters={clusters}
            selectedClusterId={selectedCluster?.id ?? null}
            onSelect={(cluster) =>
              onSelectCluster(selectedCluster?.id === cluster.id ? null : cluster)
            }
          />
        )}
      </div>

      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
          {/* Swatches are sampled straight off the brand gradient, so the legend
              can never drift away from the colours the bubbles actually use. */}
          {[
            { label: "Well covered", confidence: 1 },
            { label: "Holding up", confidence: 0.5 },
            { label: "Needs more", confidence: 0 },
          ].map((entry) => (
            <span key={entry.label} className="inline-flex items-center gap-1.5">
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: confidenceColor(entry.confidence) }}
                aria-hidden
              />
              {entry.label}
            </span>
          ))}
          <span>Bubble size shows how much you have on each topic.</span>
        </div>

        {selectedCluster && (
          <button
            type="button"
            onClick={() => onSelectCluster(null)}
            className="inline-flex items-center gap-1.5 self-start rounded-full border border-genie-primary/30 bg-genie-primary-light px-3 py-1 text-xs font-semibold text-genie-primary transition-colors hover:bg-white sm:self-auto"
          >
            Showing {selectedCluster.label} below
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      {isPartial && (
        <p className="mt-2 text-xs text-gray-500">
          Grouped from your most recent sources. Very large libraries are mapped from a recent
          slice — the file tools below still show everything.
        </p>
      )}
    </div>
  </section>
);

export default KnowledgeBrainOverview;
